import { useState } from 'react'
import { ExternalLink, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-provider'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  useCatalogSources,
  useCreateItemSource,
  useDeleteItemSource,
  useItemSources,
  useRefreshItemSource,
} from '@/features/catalog/use-sources'
import { formatUsd } from '@/features/projects/rollup-bom'
import type { ApiError, CatalogItemSource, CatalogSource, FetchErrorCode } from '@/api/types'

interface ManageSourcesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  kind: 'MATERIAL' | 'TOOL'
  globalCatalogId: string
  globalCatalogName: string
}

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as ApiError | undefined)?.detail ?? fallback

const errorLabel: Record<FetchErrorCode, string> = {
  MOCK: 'Mock-induced error',
  RATE_LIMITED: 'Rate limited',
  TIMEOUT: 'Timed out',
  PARSE_ERROR: "Couldn't parse response",
  HTTP_4XX: 'Source returned 4xx',
  HTTP_5XX: 'Source returned 5xx',
  CIRCUIT_OPEN: 'Source temporarily unavailable',
  NOT_USD: 'Source returned non-USD price',
  OTHER: 'Fetch failed',
}

const formatLastFetched = (iso: string | null): string => {
  if (!iso) return 'never'
  const dt = new Date(iso)
  return dt.toLocaleString()
}

interface ItemSourceRowProps {
  row: CatalogItemSource
  onRefresh: () => void
  onDelete: () => void
  refreshing: boolean
}

const ItemSourceRow = ({ row, onRefresh, onDelete, refreshing }: ItemSourceRowProps) => (
  <li className="flex flex-col gap-1 rounded border p-2.5 text-sm">
    <div className="flex items-center justify-between gap-2">
      <span className="truncate font-medium">{row.sourceDisplayName}</span>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onRefresh}
          disabled={refreshing || row.fetchInProgress}
          aria-label={`Refresh price from ${row.sourceDisplayName}`}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
          aria-label={`Delete binding to ${row.sourceDisplayName}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
    <a
      href={row.sourceUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1 truncate text-xs text-muted-foreground hover:underline"
    >
      <span className="truncate">{row.sourceUrl}</span>
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
    </a>
    <div className="flex items-baseline justify-between gap-2 text-xs">
      {row.lastFetchedPriceCents != null ? (
        <span className={`tabular-nums ${row.isStale ? 'text-muted-foreground' : ''}`}>
          {formatUsd(row.lastFetchedPriceCents)}
          {row.isStale && <span className="ml-1 text-[10px] uppercase">stale</span>}
        </span>
      ) : (
        <span className="text-muted-foreground">Not fetched yet</span>
      )}
      <span className="text-muted-foreground">{formatLastFetched(row.lastFetchedAt)}</span>
    </div>
    {row.lastFetchError && (
      <span className="text-xs text-destructive">{errorLabel[row.lastFetchError]}</span>
    )}
  </li>
)

interface AddSourceFormProps {
  sources: CatalogSource[]
  onSubmit: (sourceId: string, sourceUrl: string) => void
  pending: boolean
}

const AddSourceForm = ({ sources, onSubmit, pending }: AddSourceFormProps) => {
  const [sourceId, setSourceId] = useState(sources[0]?.id ?? '')
  const [sourceUrl, setSourceUrl] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = sourceUrl.trim()
    if (!sourceId || !trimmed) return
    onSubmit(sourceId, trimmed)
    setSourceUrl('')
  }

  if (sources.length === 0) {
    return <p className="text-sm text-muted-foreground">No sources configured.</p>
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2 rounded border p-3">
      <div className="grid grid-cols-[1fr_2fr] gap-2">
        <div>
          <Label htmlFor="source-select" className="text-xs">
            Source
          </Label>
          <select
            id="source-select"
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            className="mt-1 h-8 w-full rounded border bg-background px-2 text-sm"
          >
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="source-url" className="text-xs">
            URL
          </Label>
          <Input
            id="source-url"
            type="text"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="https://retailer.com/product/…"
            className="mt-1 h-8 text-sm"
          />
        </div>
      </div>
      <Button
        type="submit"
        size="sm"
        disabled={pending || !sourceId || !sourceUrl.trim()}
        className="gap-1.5"
      >
        <Plus className="h-3.5 w-3.5" />
        Add source
      </Button>
    </form>
  )
}

/**
 * Phase 16 — manage every source binding for one global catalog row. Surfaced from the catalog
 * drawer's Sources affordance on bound rows. The dialog operates on the *global* row, not the
 * per-project row — bindings are shared across every project that uses the global, matching the
 * Phase 15 "global is shared" model.
 */
export const ManageSourcesDialog = ({
  open,
  onOpenChange,
  kind,
  globalCatalogId,
  globalCatalogName,
}: ManageSourcesDialogProps) => {
  const sourcesQuery = useCatalogSources()
  const itemSourcesQuery = useItemSources(kind, globalCatalogId)
  const create = useCreateItemSource(kind, globalCatalogId)
  const refresh = useRefreshItemSource(kind, globalCatalogId)
  const remove = useDeleteItemSource(kind, globalCatalogId)
  const confirm = useConfirm()
  const [refreshingId, setRefreshingId] = useState<string | null>(null)

  const handleAdd = (sourceId: string, sourceUrl: string) => {
    create.mutate(
      { sourceId, globalCatalogKind: kind, globalCatalogId, sourceUrl },
      {
        onSuccess: () => toast.success('Source added'),
        onError: (e) => toast.error(apiErrorMessage(e, 'Failed to add source')),
      }
    )
  }

  const handleRefresh = (id: string) => {
    setRefreshingId(id)
    refresh.mutate(id, {
      onSuccess: (row) => {
        if (row.lastFetchError) {
          toast.error(`${row.sourceDisplayName}: ${errorLabel[row.lastFetchError]}`)
        } else {
          toast.success(
            row.lastFetchedPriceCents != null
              ? `${row.sourceDisplayName}: ${formatUsd(row.lastFetchedPriceCents)}`
              : 'Refreshed'
          )
        }
      },
      onError: (e) => toast.error(apiErrorMessage(e, 'Refresh failed')),
      onSettled: () => setRefreshingId(null),
    })
  }

  const handleDelete = async (row: CatalogItemSource) => {
    const ok = await confirm({
      title: `Remove ${row.sourceDisplayName} as a source?`,
      description: `Pricing from this source will no longer apply to "${globalCatalogName}".`,
      confirmText: 'Remove',
      destructive: true,
    })
    if (!ok) return
    remove.mutate(row.id, {
      onSuccess: () => toast.success('Source removed'),
      onError: (e) => toast.error(apiErrorMessage(e, 'Failed to remove source')),
    })
  }

  const rows = itemSourcesQuery.data ?? []
  const sources = sourcesQuery.data ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Sources for {globalCatalogName}</DialogTitle>
          <DialogDescription>
            Bind retailer URLs to this shared catalog row to track live prices. Deletes affect every
            project that uses this global row.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <AddSourceForm sources={sources} onSubmit={handleAdd} pending={create.isPending} />

          <div>
            <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Active sources
            </h4>
            {itemSourcesQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No sources yet. Add one above to start tracking prices.
              </p>
            ) : (
              <ul className="space-y-2">
                {rows.map((row) => (
                  <ItemSourceRow
                    key={row.id}
                    row={row}
                    onRefresh={() => handleRefresh(row.id)}
                    onDelete={() => handleDelete(row)}
                    refreshing={refreshingId === row.id}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
